'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { getEstateCatalog } from '@/forApp/api/getEstate';
import { getMainFiltres } from '@/forApp/api/getMainFilters';
import { useApp } from '@/forApp/states/appState/appState';
import type { RealEstateCatalogResponseT } from '@/forApp/types/Entity';
import Breadcrumbs from '@/shared/Breadcrumbs';
import Button from '@/shared/Buttons/Button';
import { Icons } from '@/shared/Icons/Icons';
import Catalog from '@/widgets/Catalog/Catalog';
import CatalogSkeleton from '@/widgets/Catalog/CatalogSkeleton/CatalogSkeleton';
import CatalogMobile from '@/widgets/Catalog/mobile/CatalogMobile';
import type { DefaultFilterT } from '@/widgets/MainBlock/MainSearch/types';
import { useSetSearchParams } from '@/forApp/utils/hooks/useSearchParams';
import { DEFAULT_SORT } from '@/forApp/utils/constants';
import { t } from '@/forApp/utils/translations';

import styles from './styles.module.css';

type ClientCatalogPageProps = {
  type: string;
  searchParams?: { [key: string]: string | string[] };
};

const ClientCatalogPage: React.FC<ClientCatalogPageProps> = ({ type }) => {
  const typeTitle = t(type) || t('real-estate');

  const [mainFilters, setMainFilters] = useState<DefaultFilterT | null>(null);
  const [catalog, setCatalog] = useState<RealEstateCatalogResponseT | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [isLoad, setLoad] = useState<boolean>(false);
  const [isOpen, setIsOpenFilters] = useState<boolean>(false);
  const router = useRouter();
  const pathname = usePathname();

  const [sp] = useSetSearchParams();
  const spString = sp?.toString() || '';
  const normalizedSearchParams = useMemo(() => {
    return {
      sort: DEFAULT_SORT,
      ...sp.toObject(),
    };
  }, [sp]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setMainFilters(null);
    setCatalog(null);

    async function load() {
      try {
        const [filtersRes, catalogRes] = await Promise.all([
          getMainFiltres(type),
          getEstateCatalog(type, normalizedSearchParams),
        ]);
        if (!cancelled) {
          setMainFilters(filtersRes);
          setCatalog(catalogRes);
        }
      } catch (e) {
        if (!cancelled)
          setError((e as Error)?.message || 'Ошибка загрузки каталога');
      } finally {
        setLoad(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, spString]);

  const mapHref = useMemo(() => {
    const base = typeof pathname === 'string' ? pathname : '';
    const q = spString ? `?${spString}` : '';
    return `/maps${base.startsWith('/') ? base : `/${base}`}${q}`;
  }, [pathname, spString]);

  const { isMobile } = useApp();

  const crumbs = [
    {
      link: '/',
      title: 'Главная',
    },
    {
      title: typeTitle,
    },
  ];

  if (isMobile) {
    return (
      <div className={styles.mobileWrap}>
        <Breadcrumbs crumbs={crumbs} />
        <h1 className={styles.typeCatalogTitleMobile}>{typeTitle}</h1>
        <div className={styles.btnBlock}>
          <Button
            text={'Фильтры'}
            icon={<Icons.Filter className={styles.iconInherit} />}
            iconPosition={'left'}
            color={'white'}
            onClick={() => setIsOpenFilters(true)}
          />
          <Button
            text={'Показать на карте'}
            icon={<Icons.Maps className={styles.iconInherit} />}
            iconPosition={'left'}
            color={'white'}
            onClick={() => {
              router.push(mapHref);
            }}
          />
        </div>
        <Suspense fallback={<CatalogSkeleton />}>
          {error ? (
            <div>Произошла ошибка: {error}</div>
          ) : mainFilters && catalog && !isLoad ? (
            <CatalogMobile
              defaultFilter={mainFilters}
              catalog={catalog}
              type={type}
              setLoad={setLoad}
              isOpenFilter={isOpen}
              setIsOpenFilter={setIsOpenFilters}
            />
          ) : (
            <div className={styles.wrapSkeleton}>
              <CatalogSkeleton />
            </div>
          )}
        </Suspense>
      </div>
    );
  } else {
    return (
      <div>
        <Breadcrumbs crumbs={crumbs} />
        <div className={styles.headerRow}>
          <h1 className={styles.typeCatalogTitle}>{typeTitle}</h1>
          <Link href={mapHref} className={styles.mapLink} prefetch={false}>
            <span className={styles.iconWrap}>
              <Icons.Maps className={styles.iconInherit} />
            </span>
            <span>Показать на карте</span>
          </Link>
        </div>
        <Suspense fallback={<CatalogSkeleton />}>
          {error ? (
            <div>Произошла ошибка: {error}</div>
          ) : mainFilters && catalog && !isLoad ? (
            <Catalog
              defaultFilter={mainFilters}
              catalog={catalog}
              type={type}
              setLoad={setLoad}
            />
          ) : (
            <CatalogSkeleton />
          )}
        </Suspense>
      </div>
    );
  }
};

export default ClientCatalogPage;
